import { openDialog } from './foundation.js';
import { canPurchase, cartAjaxUrl, variantUrl } from './product.js';

const dialog = document.querySelector('[data-quick-view-dialog]');
if (dialog) {
  const content = dialog.querySelector('[data-quick-view-content]');
  const status = dialog.querySelector('[data-quick-view-status]');
  const fallback = dialog.querySelector('[data-quick-view-fallback]');
  let closeDialog = null;
  let request = null;
  let addRequest = null;
  let sequence = 0;
  let busy = false;
  let opener = null;

  const product = () => content.querySelector('[data-quick-view-product]');
  const state = () => ({ id: product()?.dataset.variantId || '', available: product()?.dataset.available === 'true', requiresSellingPlan: product()?.dataset.requiresSellingPlan === 'true' });
  const form = () => content.querySelector('[data-quick-view-form]');
  const selectedIds = () => [...content.querySelectorAll('[data-product-option-group]')].map(group => group.querySelector('[data-product-option][aria-current="true"]')?.dataset.optionValueId || group.querySelector('option:checked')?.dataset.optionValueId || '');
  function sync() {
    const current = state();
    const input = form()?.querySelector('[data-quick-view-variant-id]');
    if (input) input.value = current.id;
    const plan = form()?.querySelector('[data-quick-view-plan]')?.value || '';
    const add = content.querySelector('[data-quick-view-add]');
    if (add) add.disabled = busy || !canPurchase(current, plan);
  }
  async function load(url, expectedId = '') {
    request?.abort();
    request = new AbortController();
    const thisRequest = request;
    const ticket = ++sequence;
    busy = true;
    sync();
    status.textContent = dialog.dataset.loading;
    try {
      const target = new URL(url, location.origin);
      if (target.origin !== location.origin || !/\/(?:[^/]+\/)*products\/[^/]+$/.test(target.pathname)) throw new Error('Invalid product URL');
      const publicUrl = `${target.pathname}${target.search}`;
      fallback.href = publicUrl;
      target.searchParams.set('section_id', 'quick-view-product');
      const preview = /^(?:home|style)-([a-z]+)$/.exec((location.href ? new URL(location.href).searchParams.get('view') : '') || globalThis.document?.documentElement?.dataset?.previewSuffix || '');
      if (preview) target.searchParams.set('view', `style-${preview[1]}`);
      const response = await fetch(target, { signal: thisRequest.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Section ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const rendered = html.querySelector('[data-quick-view-product]');
      if (!rendered || (expectedId && rendered.dataset.productId !== expectedId)) throw new Error('Product section missing');
      if (thisRequest.signal.aborted || ticket !== sequence) return;
      content.replaceChildren(rendered);
      const full = rendered.querySelector('[data-quick-view-full]');
      if (full) full.href = variantUrl(publicUrl, rendered.dataset.variantId, selectedIds().join(','), location.origin);
      status.textContent = '';
      fallback.hidden = true;
    } catch (error) {
      if (!thisRequest.signal.aborted && ticket === sequence) {
        status.textContent = dialog.dataset.error;
        if (!expectedId) content.replaceChildren();
        fallback.hidden = false;
      }
    } finally {
      if (ticket === sequence) { busy = false; sync(); }
    }
  }
  function open(trigger) {
    const url = trigger.dataset.productUrl;
    if (!url) return;
    if (dialog.open) closeDialog?.();
    opener = trigger;
    content.replaceChildren();
    fallback.href = url;
    fallback.hidden = false;
    status.textContent = dialog.dataset.loading;
    closeDialog = openDialog(dialog, trigger);
    document.body.classList.add('quick-view-open');
    load(url);
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-quick-view-open]');
    if (trigger) { event.preventDefault(); open(trigger); }
  });
  dialog.addEventListener('click', event => { if (event.target === dialog || event.target.closest('[data-quick-view-close]')) closeDialog?.(); });
  dialog.addEventListener('close', () => { request?.abort(); addRequest?.abort(); addRequest = null; busy = false; sequence++; document.body.classList.remove('quick-view-open'); content.replaceChildren(); status.textContent = ''; closeDialog = null; });
  dialog.addEventListener('click', event => {
    const photo = event.target.closest('[data-quick-view-photo]');
    if (photo) {
      const image = content.querySelector('[data-quick-view-media] > img');
      if (image) { image.srcset = ''; image.src = photo.dataset.quickViewPhoto; image.alt = photo.dataset.photoAlt || ''; }
      for (const button of content.querySelectorAll('[data-quick-view-photo]')) button.setAttribute('aria-pressed', String(button === photo));
      return;
    }
    const step = event.target.closest('[data-quick-view-step]');
    if (step) {
      const input = content.querySelector('[data-quick-view-quantity]');
      input.value = Math.max(1, (Number.parseInt(input.value, 10) || 1) + Number(step.dataset.quickViewStep));
      return;
    }
    const option = event.target.closest('[data-product-option]');
    if (!option || !content.contains(option)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const values = selectedIds();
    values[Number(option.dataset.optionPosition) - 1] = option.dataset.optionValueId;
    const url = new URL(option.dataset.productUrl || product().dataset.productUrl, location.origin);
    url.searchParams.set('option_values', values.join(','));
    load(url, option.dataset.productUrl ? '' : product().dataset.productId);
  });
  dialog.addEventListener('change', event => {
    if (event.target.matches('[data-product-option-select]')) {
      const choice = event.target.selectedOptions[0];
      const values = selectedIds();
      values[Number(event.target.closest('[data-option-position]')?.dataset.optionPosition) - 1] = choice?.dataset.optionValueId || '';
      const url = new URL(choice?.dataset.productUrl || product().dataset.productUrl, location.origin);
      url.searchParams.set('option_values', values.join(','));
      load(url, choice?.dataset.productUrl ? '' : product().dataset.productId);
    }
    if (event.target.matches('[data-quick-view-plan]')) sync();
  });
  dialog.addEventListener('submit', async event => {
    if (!event.target.matches('[data-quick-view-form]')) return;
    const current = state();
    const plan = form()?.querySelector('[data-quick-view-plan]')?.value || '';
    if (busy || !canPurchase(current, plan)) { event.preventDefault(); return; }
    event.preventDefault();
    busy = true; sync();
    const submitted = new FormData(event.target);
    const controller = new AbortController();
    addRequest = controller;
    try {
      const response = await fetch(cartAjaxUrl(event.target.action), { method: 'POST', body: submitted, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, signal: controller.signal });
      if (!response.ok) throw new Error(`Cart ${response.status}`);
      await response.json();
      if (controller.signal.aborted) return;
      const trigger = opener;
      addRequest = null;
      closeDialog?.();
      document.dispatchEvent(new CustomEvent('nawiya:cart:changed', { detail: { source: 'quick-view', trigger, variantId: current.id, quantity: Number(submitted.get('quantity') || 1) } }));
    } catch { if (!controller.signal.aborted) status.textContent = dialog.dataset.addError; }
    finally { if (addRequest === controller) { addRequest = null; busy = false; sync(); } }
  });
  document.addEventListener('shopify:section:unload', event => { if (event.target.contains(dialog)) closeDialog?.(); });
}
